import { apiClients } from '../apiClients';
import offlineConfig from '../assets/data/offlineConfig.json';
import { TwitchHelixService } from '../services/TwitchHelixService';
import { Configuration } from '../types/Configuration';
import {
	TwitchExtensionInitializationError,
	VoicePreviewError,
} from '../types/errors';
import { CustomError } from '../types/errors/CustomError';
import { TwitchUserAuth } from '../types/TwitchExtension';

import { TwitchStore } from './types/TwitchStore';
import { TwitchStoreActions } from './types/TwitchStoreActions';
import { DEFAULT_VOLUME } from './state';

const INITIALIZATION_TIMEOUT_MS = 8000;
const LIVE_STATUS_CHECK_INTERVAL_MS = 1000 * 15;

export const actions: TwitchStoreActions = {
	authorizationReceived(this: TwitchStore, broadcaster: TwitchUserAuth) {
		this.broadcaster = broadcaster;

		apiClients.streamerApi?.setAuthorization(`Bearer ${broadcaster.token}`);
		apiClients.twitchHelixApi?.setAuthorization(
			`Extension ${broadcaster.helixToken}`,
		);
		apiClients.twitchHelixApi?.setHeaders({
			'Client-ID': broadcaster.clientId,
		});
	},

	configurationReceived(
		this: TwitchStore,
		{ voices, isEnabled, defaultTimeSeconds },
	) {
		this.voices = voices;
		this.isEnabled = isEnabled;
		this.defaultTimeSeconds = defaultTimeSeconds;

		if (!this.selectedVoiceId) {
			return;
		}

		if (!voices.find((voice) => voice.id === this.selectedVoiceId)) {
			this.clearSelectedVoice();
		}
	},

	startInitializationTimeout(this: TwitchStore) {
		window.setTimeout(() => {
			if (this.voices?.length) {
				return;
			}

			const error = TwitchExtensionInitializationError.report({
				description: 'Initialization timeout',
			});
			this.showError(error);
			this.forceOfflineMode();
		}, INITIALIZATION_TIMEOUT_MS);
	},

	forceOfflineMode(this: TwitchStore) {
		const configuration: Configuration = {
			...offlineConfig,
			voices: this.voices ?? offlineConfig.voices,
		};

		this.configurationReceived(configuration);
	},

	updateLiveStatus(this: TwitchStore) {
		if (!this.broadcaster) {
			return;
		}

		TwitchHelixService.isStreamerLive(this.broadcaster.channelId)
			.then((isLive) => {
				this.isStreaming = isLive;
			})
			.catch(() => {
				this.isStreaming = false;
			});
	},

	stopLiveStatusCheck(this: TwitchStore) {
		if (!this.liveStatusCheckIntervalId) {
			return;
		}

		window.clearInterval(this.liveStatusCheckIntervalId);
		this.liveStatusCheckIntervalId = undefined;
	},

	startLiveStatusCheck(this: TwitchStore) {
		this.stopLiveStatusCheck();
		this.updateLiveStatus();

		this.liveStatusCheckIntervalId = window.setInterval(
			this.updateLiveStatus,
			LIVE_STATUS_CHECK_INTERVAL_MS,
		);

		window.removeEventListener('beforeunload', this.stopLiveStatusCheck);
		window.addEventListener('beforeunload', this.stopLiveStatusCheck);
	},

	async voiceSelected(this: TwitchStore, voiceId: string) {
		this.clearSelectedVoice();

		this.selectedVoiceId = voiceId;

		await this.previewSelectedVoice();
	},

	async previewSelectedVoice(this: TwitchStore) {
		this.voicePreview?.pause();
		this.voicePreviewProgress = undefined;

		try {
			this.voicePreview = new Audio(this.selectedVoice?.previewUrl);
			this.voicePreview.volume = this.volume;
			await this.voicePreview.play();
			this.voicePreviewProgress = 0;
		} catch (error) {
			VoicePreviewError.report({
				description: 'Error playing voice preview',
				cause: error,
			}).debug();

			this.voicePreview = undefined;
			this.voicePreviewProgress = 100;

			return;
		}

		this.voicePreview.ontimeupdate = () => {
			if (!this.voicePreview) {
				return;
			}

			this.voicePreviewProgress =
				(this.voicePreview.currentTime / this.voicePreview.duration) * 100;
		};
	},

	clearSelectedVoice(this: TwitchStore) {
		this.voicePreview?.pause();
		this.selectedVoiceId = undefined;
		this.voicePreview = undefined;
		this.voicePreviewProgress = undefined;
	},

	volumeChanged(this: TwitchStore, volume: number) {
		if (!this.voicePreview) {
			return;
		}

		this.volume = volume;
		this.voicePreview.volume = this.volume;
	},

	muteToggled(this: TwitchStore) {
		this.volume = this.volume > 0 ? 0 : DEFAULT_VOLUME;

		if (!this.voicePreview) {
			return;
		}
		this.voicePreview.volume = this.volume;
	},

	processingStarted(this: TwitchStore) {
		this.isProcessing = true;
	},

	processingFinished(this: TwitchStore) {
		this.isProcessing = false;
		this.clearSelectedVoice();
	},

	processingCanceled(this: TwitchStore, error?: CustomError) {
		this.isProcessing = false;

		if (error) {
			this.showError(error);
		}

		this.clearSelectedVoice();
	},

	showError(
		this: TwitchStore,
		error: CustomError | { message: string; name?: string },
	) {
		this.error = {
			message: error.message,
			translationKey: `error.message.${error.name ?? 'generic'}`,
		};
	},

	clearError(this: TwitchStore) {
		this.error = undefined;
	},
};
